import {
  Button,
  Alert,
  Combobox,
  MultiSelect,
  FormField,
  TextInput,
  TextArea,
  SuccessNotice,
} from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Theme, ThemeFamily, ThemeStatus } from "@/lib/api/types";
import { THEME_FAMILIES, THEME_NICHES } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateThemeMutation } from "../services/useUpdateThemeMutation";

const FAMILY_LABEL_KEY: Record<ThemeFamily, string> = {
  starter: keys.themes.families.starter,
};

const STATUS_LABEL_KEY: Record<ThemeStatus, string> = {
  DRAFT: keys.themes.draft,
  PUBLISHED: keys.themes.published,
  ARCHIVED: keys.themes.archived,
};

const STATUS_VALUES: ThemeStatus[] = ["DRAFT", "PUBLISHED", "ARCHIVED"];

export function ThemeEditForm({ theme }: { theme: Theme }) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [family, setFamily] = useState<ThemeFamily>(theme.family);
  const [status, setStatus] = useState<ThemeStatus>(theme.status);
  const [niches, setNiches] = useState<string[]>(theme.niches);
  const { mutateAsync: updateTheme, isPending } = useUpdateThemeMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);

    const nameAr = String(formData.get("nameAr") ?? "").trim();
    const nameEn = String(formData.get("nameEn") ?? "").trim();
    const nextFieldErrors: Record<string, string> = {};
    if (nameAr.length === 0) {
      nextFieldErrors.nameAr = t(keys.errors.required);
    }
    if (nameEn.length === 0) {
      nextFieldErrors.nameEn = t(keys.errors.required);
    }
    const sortWeightRaw = String(formData.get("sortWeight") ?? "").trim();
    const sortWeight =
      sortWeightRaw.length > 0 ? Number(sortWeightRaw) : undefined;
    if (sortWeight !== undefined && !Number.isFinite(sortWeight)) {
      nextFieldErrors.sortWeight = t(keys.errors.required);
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    const descriptionAr = String(formData.get("descriptionAr") ?? "").trim();
    const descriptionEn = String(formData.get("descriptionEn") ?? "").trim();

    try {
      await updateTheme({
        themeId: theme.id,
        family,
        status,
        nameAr,
        nameEn,
        descriptionAr,
        descriptionEn,
        niches,
        sortWeight,
      });
    } catch (submitError) {
      if (submitError instanceof ApiJsonClientError) {
        setError(submitError.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }

    setSuccess(true);
  };

  return (
    <form
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      {success ? <SuccessNotice title={t(keys.common.updateSuccess)} /> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField
          label={t(keys.themes.family)}
          name="family"
          required
          error={fieldErrors.family}
        >
          <Combobox
            options={THEME_FAMILIES.map((value) => ({
              value,
              label: t(FAMILY_LABEL_KEY[value]),
            }))}
            value={family}
            onChange={(value) => setFamily((value as ThemeFamily) ?? "starter")}
          />
        </FormField>
        <FormField label={t(keys.common.status)} name="status">
          <Combobox
            options={STATUS_VALUES.map((value) => ({
              value,
              label: t(STATUS_LABEL_KEY[value]),
            }))}
            value={status}
            onChange={(value) => setStatus((value as ThemeStatus) ?? "DRAFT")}
          />
        </FormField>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField
          label={t(keys.themes.nameAr)}
          name="nameAr"
          required
          error={fieldErrors.nameAr}
        >
          <TextInput
            name="nameAr"
            dir="rtl"
            defaultValue={theme.nameAr}
            required
            hasError={Boolean(fieldErrors.nameAr)}
          />
        </FormField>
        <FormField
          label={t(keys.themes.nameEn)}
          name="nameEn"
          required
          error={fieldErrors.nameEn}
        >
          <TextInput
            name="nameEn"
            dir="ltr"
            defaultValue={theme.nameEn}
            required
            hasError={Boolean(fieldErrors.nameEn)}
          />
        </FormField>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField label={t(keys.themes.descriptionAr)} name="descriptionAr">
          <TextArea
            name="descriptionAr"
            dir="rtl"
            rows={3}
            defaultValue={theme.descriptionAr ?? ""}
          />
        </FormField>
        <FormField label={t(keys.themes.descriptionEn)} name="descriptionEn">
          <TextArea
            name="descriptionEn"
            dir="ltr"
            rows={3}
            defaultValue={theme.descriptionEn ?? ""}
          />
        </FormField>
      </div>
      <FormField label={t(keys.themes.niches)} name="niches">
        <MultiSelect
          options={THEME_NICHES.map((value) => ({
            value,
            label: t(keys.themes.nicheLabels[value]),
          }))}
          value={niches}
          onChange={setNiches}
          placeholder={t(keys.themes.nichesPlaceholder)}
          aria-label={t(keys.themes.niches)}
        />
      </FormField>
      <FormField
        label={t(keys.themes.sortWeight)}
        name="sortWeight"
        error={fieldErrors.sortWeight}
      >
        <TextInput
          name="sortWeight"
          type="number"
          inputMode="numeric"
          dir="ltr"
          defaultValue={String(theme.sortWeight)}
          hasError={Boolean(fieldErrors.sortWeight)}
        />
      </FormField>
      <Button
        type="submit"
        disabled={isPending}
        variant="primary"
        className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {t(keys.common.update)}
      </Button>
    </form>
  );
}
